import { useEffect, useState } from "react"
import { LucideIcon } from "lucide-react"

interface Props {
  label: string
  icon: LucideIcon
  value: number
  included?: number
  overageCost?: number
  isLoading: boolean
  isError: boolean
  compact?: boolean
  index: number
}

const fmt = (n: number, compact?: boolean): string => {
  if (compact) {
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`
  }
  return n.toLocaleString()
}

const getBarColor = (ratio: number): string => {
  if (ratio > 1) return "#D96060"
  if (ratio > 0.8) return "#F59E0B"
  return "#63C7C4"
}

const UsageMeter: React.FC<Props> = ({
  label,
  icon: Icon,
  value,
  included,
  overageCost = 0,
  isLoading,
  isError,
  compact,
  index
}) => {
  const [barWidth, setBarWidth] = useState(0)

  const hasLimit = included !== undefined && included > 0
  const ratio = hasLimit ? value / included : 0
  const percentage = Math.round(ratio * 100)
  const overage = hasLimit ? Math.max(0, value - included) : 0
  const remaining = hasLimit ? Math.max(0, included - value) : 0
  const barColor = getBarColor(ratio)

  useEffect(() => {
    const timer = setTimeout(
      () => setBarWidth(Math.min(ratio * 100, 100)),
      150 + index * 80
    )
    return () => clearTimeout(timer)
  }, [ratio, index])

  if (isLoading) {
    return (
      <div
        className="py-5 opacity-0 animate-fade-slide-in"
        style={{ animationDelay: `${index * 80}ms` }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
          <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="h-2 bg-gray-100 rounded-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div
        className="py-5 opacity-0 animate-fade-slide-in"
        style={{ animationDelay: `${index * 80}ms` }}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-300" />
          <span className="text-sm text-gray-400">{label}</span>
          <span className="text-xs text-red-400 ml-auto">Unavailable</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className="py-5 opacity-0 animate-fade-slide-in"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-600">{label}</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-sm font-extrabold text-base-dark"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {fmt(value, compact)}
          </span>
          {hasLimit && (
            <span className="text-xs text-gray-400">
              / {fmt(included, compact)}
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {hasLimit && (
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${barWidth}%`,
              backgroundColor: barColor,
              transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          />
        </div>
      )}

      {/* Footer row */}
      {hasLimit && (
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">{percentage}% used</span>
          {overage > 0 ? (
            <span className="text-xs text-red-400 font-medium">
              +{fmt(overage, compact)} over
              {overageCost > 0 && (
                <span className="ml-1.5 text-red-400">
                  · ${overageCost.toFixed(2)}
                </span>
              )}
            </span>
          ) : (
            <span className="text-xs text-gray-400">
              {fmt(remaining, compact)} remaining
            </span>
          )}
        </div>
      )}

      {/* Value-only display when no limit */}
      {!hasLimit && (
        <div className="flex items-baseline justify-between mt-1">
          <span
            className="text-2xl font-extrabold text-base-dark"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {fmt(value, compact)}
          </span>
          {overageCost > 0 && (
            <span className="text-xs text-gray-400">
              ~${overageCost.toFixed(2)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default UsageMeter
